import React, { useState } from "react";
import PropTypes from "prop-types";
import { useProfile } from "@/_actions/userActions";
import EditProfile from "./forms/EditProfile";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Phone, Mail, Edit, MessageSquare, Loader2 } from "lucide-react";

export default function UserProfile({ dashboard = false }) {
  const { data: profile = {}, isLoading, mutate: profileMutate } = useProfile();
  const [showEditProfile, toggleEditProfile] = useState(false);
  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");

  return (
    <div className="@container space-y-4">
      {dashboard ? (
        <h2 className="text-sm font-semibold uppercase text-slate-400">Información de contacto</h2>
      ) : (
        <>
          <p className="mt-1 block text-xl font-medium leading-tight text-black">
            Datos del Usuario
          </p>
          <p className="mt-2 text-gray-500">
            Estos datos corresponden al usuario que administra esta cuenta.
          </p>
        </>
      )}
      {isLoading ? (
        <div className="flex justify-center items-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : profile && !showEditProfile ? (
        dashboard ? (
          <div className="space-y-4">
            <p className="break-words text-xl font-semibold uppercase leading-tight text-gray-900">
              {fullName || "Usuario"}
            </p>
            <div className="flex flex-wrap items-start gap-4">
              <Avatar className="size-16 bg-white @min-[420px]:size-20">
                <AvatarImage
                  src={profile.logo_client || undefined}
                  alt="Logo de la cuenta"
                  className="object-contain"
                />
                <AvatarFallback className="bg-[#6067BE] text-3xl font-semibold text-white">
                  {(fullName || profile.email || "U").trim().charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 space-y-3 text-sm text-gray-700 @min-[420px]:text-base">
                <div className="flex items-center gap-3">
                  <Phone aria-hidden="true" className="size-5 shrink-0" />
                  <span className="break-all">{profile?.contact?.mobile || "—"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MessageSquare aria-hidden="true" className="size-5 shrink-0" />
                  <span className="break-all">{profile?.contact?.whatsapp || "—"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail aria-hidden="true" className="size-5 shrink-0" />
                  <span className="break-all">{profile.email || "—"}</span>
                </div>
              </div>
              <div className="w-full @min-[420px]:w-auto">
                <Button
                  onClick={() => toggleEditProfile(true)}
                  type="button"
                  variant="outline"
                  data-splitbee-event="account-edit-contact"
                >
                  Editar
                  <Edit aria-hidden="true" className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-wide text-blue-800">
              {profile && profile.first_name} {profile && profile.last_name}
            </div>

            <div className="flex items-center">
              <Phone className="h-4 w-4 text-gray-400" />
              <span className="ml-2 text-sm">
                {profile ? profile?.contact?.mobile : "------------"}
              </span>
            </div>

            <div className="flex items-center">
              <MessageSquare className="h-4 w-4 text-gray-400" />
              <span className="ml-2 text-sm">
                {profile ? profile?.contact?.whatsapp : "------------"}
              </span>
            </div>

            <div className="flex items-center">
              <Mail className="h-4 w-4 text-gray-400" />
              <span className="ml-2 text-sm">{profile ? profile.email : "------------"}</span>
            </div>

            <Button
              onClick={() => toggleEditProfile(true)}
              type="button"
              data-splitbee-event="account-edit-contact"
              className="mt-5"
            >
              <span className="inline">Editar </span>
              <Edit className="ml-2 -mr-0.5 inline h-4 w-4" />
            </Button>
          </div>
        )
      ) : null}

      {showEditProfile ? (
        <EditProfile
          data={profile}
          callBack={() => {
            toggleEditProfile(false);
            profileMutate();
          }}
          cancel={() => toggleEditProfile(false)}
        />
      ) : null}
    </div>
  );
}

UserProfile.propTypes = {
  dashboard: PropTypes.bool,
};
